import React, { useMemo } from 'react';
import {
	View,
	StyleSheet,
	useWindowDimensions,
	ScrollView,
	Image,
	TouchableHighlight,
	Platform,
} from 'react-native';
import colors from '../../../config/colors';
import moment from 'moment';
import Typography from '../../../components/shared/Typography/Typography.component';
import I18n from '../../../components/shared/i18n/I18n.component';
import WorkLog from '../../../components/shared/ClockReport/Record/Record.component';
import { Record } from '../../../components/shared/ClockReport/Record/Record.component';
import ImageIcon from '../../../components/shared/Icon/ImageIcon.component';
import getShadowStyle from '../../../config/shadow';
import { i18nService } from '../../../services/i18n.service';
import { toNumber } from 'lodash';
import ActionButtonContainer from '../../../components/shared/Buttons/ActionButton.container';
const imageEmptyState = require('../../../../assets/images/reportEmptyState/empty.png');

const convertToHoursFormat = time => {
	if (!time) return '00:00';
	const [hours, minutes, seconds] = time.split(':');
	return `${toNumber(hours).toLocaleString()}:${minutes}`;
};

const convertHoursFormat = (
	minutesValue: any,
	format?: string,
	inminutes?: boolean,
) => {
	if (minutesValue) {
		const value = inminutes ? minutesValue : Math.floor(minutesValue * 60);
		if (format === 'HH:mm') {
			const hours = `${Math.floor(value / 60)?.toLocaleString()}`;
			const minutes =
				value % 60 !== 0
					? value % 60 < 10
						? `0${value % 60}`
						: `${value % 60}`
					: '00';
			return `${hours}:${minutes}`;
		}
		return `${(value / 60).toFixed(2)}`;
	}
	return '00:00';
};

interface Props {
	reportSelectedDate: any[];
	openDrawer: any;
	onDelete: Function;
	onEdit: Function;
	hideButton: boolean;
	addWorkLog: Function;
	standardHours: any;
	totalReported: any;
	isToday: boolean;
	selectedDay: any;
	canEdit: boolean;
	splitCards?: boolean;
}

const DayReportsWeb = ({
	canEdit,
	onDelete,
	onEdit,
	hideButton,
	addWorkLog,
	standardHours,
	totalReported,
	isToday,
	selectedDay,
	reportSelectedDate,
	splitCards,
}: Props) => {
	const windowWidth = useWindowDimensions().width;
	const windowHeight = useWindowDimensions().height;
	const maxWidth = windowWidth > 1700 ? 612 : windowWidth > 1500 ? 540 : 480;
	const contentBlock = splitCards
		? {
				flex: 1,
				backgroundColor: colors.white,
				width: '100%',
				gap: 24,
				padding: 20,
				borderRadius: 8,
				...Platform.select({
					web: { gap: 16, boxShadow: '2px 2px 16px 0 rgba(0, 0, 0, 0.04)' },
					ios: { shadowColor: '#000', shadowOffset: { width: 2, height: 2 }, shadowOpacity: 0.04, shadowRadius: 16 },
					android: { elevation: 2 },
				}),
		  }
		: {
				flex: 1,
				height: windowHeight - 167,
				backgroundColor: colors.white,
				width: '100%',
				padding: 20,
				borderRadius: 8,
				...Platform.select({
					web: { gap: 16, boxShadow: '2px 2px 16px 0 rgba(0, 0, 0, 0.04)' },
					ios: { shadowColor: '#000', shadowOffset: { width: 2, height: 2 }, shadowOpacity: 0.04, shadowRadius: 16 },
					android: { elevation: 2 },
				}),
		  };
	const isTotalReportedSmallerThanStandard = useMemo(() => {
		let standard = 0;
		if (standardHours?.fixHours) {
			standard =
				moment(standardHours.fixHours, 'HH:mm:ss').diff(
					moment('00:00', 'HH:mm'),
					'minutes',
				) / 60;
		}

		return totalReported < standard;
	}, [totalReported, standardHours]);
	const standard = useMemo(() => {
		return convertToHoursFormat(standardHours?.fixHours);
	}, [standardHours]);

	return (
		<View style={contentBlock}>
			<View nativeID='detailsHeader' style={styles.detailsHeader}>
				<View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
					{!hideButton && canEdit && (
						<ActionButtonContainer onPress={addWorkLog} overrideStyle={styles.addButton} hoverStyle={{backgroundColor: colors.primaryDark}}>
						<I18n size={16} weight='normal' color='white'>
							general.add
						</I18n>
						<ImageIcon name='plusWhite' height={18} width={18} color='white' style={{ marginLeft: 4 }} />
					</ActionButtonContainer>
					)}
					<View style={{ flexDirection: 'row', alignItems: 'center'}}>
						<Typography size={14} color='clockRecordBlue' font='medium'>
							{convertHoursFormat(totalReported, 'HH:mm', false)}
						</Typography>
						<Typography size={14} color='greyText' weight='normal'>
							{`/${standard}`}
						</Typography>
					</View>
				</View>
				<View style={{ flexDirection: 'row', alignItems: 'center', gap: 16 }}>
					<I18n
						size={14}
						color='greyText'
						weight='normal'
					>
						{selectedDay}
					</I18n>

					<I18n
						size={14}
						color='primaryDarkBlue'
						font='medium'
					>
						{i18nService.translate( 'reportView.reportFrom')} 
					</I18n>
				</View>

				
			</View>
			{reportSelectedDate?.length ? (
				<View style={{ flex: 1, width: '100%', marginTop: 10 }}>
					<View
						nativeID='tableHolder'
						style={{
							width: '100%',
							justifyContent: 'flex-end',
							flex: 1,
						}}
					>
						
						<ScrollView
							nativeID='tableHolder'
							style={styles.tableHolder}
						>
							{reportSelectedDate?.map((record: Record) => (
								<WorkLog
									onEdit={onEdit}
									onDelete={onDelete}
									isToday={isToday}
									key={record.workLogId}
									record={record}
								/>
							))}
						</ScrollView>
					</View>
				</View>
			) : (
				<View
					style={{
						justifyContent: 'center',
						alignItems: 'center',
						flex: 1,
					}}
				>
					<Image
						source={imageEmptyState}
						fadeDuration={0}
						style={{
							height: 100,
							width: 100,
							marginTop: 50,
						}}
					/>
					<I18n size={14} color={'greyText'} weight='400'>
						{isToday
							? 'reportView.noWorklogsToday'
							: 'general.noLogsForThisDate'}
					</I18n>
				</View>
			)}
		</View>
	);
};
const styles = StyleSheet.create({
	line: {
		opacity: 0.2,
		width: '95%',
		height: 0,
		borderTopColor: '#525962',
		borderTopWidth: 2,
		marginVertical: 5,
	},
	recordLine: {
		opacity: 0.2,
		width: '100%',
		height: 0,
		borderTopColor: '#525962',
		borderTopWidth: 2,
		marginVertical: 5,
	},
	container: {
		flex: 1,
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-evenly',
		height: 'calc(100vh - 90px)',
	},
	detailsHeader: {
		width: '100%',
		flexDirection: 'row',
		justifyContent: 'space-between',
	},
	addButtonHolder: {
		flexDirection: 'row',
		width: '100%',
		padding: 10,
	},
	addButton: {
		backgroundColor: colors.primary,
	},
	columnHeaderRow: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: 6,
		marginHorizontal: 15,
		borderBottomWidth: 1,
		borderBottomColor: '#E2EDF4',
	},
	columnHeader: {
		flex: 1,
		alignItems: 'center',
	},
	tableHolder: {
		width: '100%',
		minHeight: 140,
		direction: 'ltr',
		paddingHorizontal: 15,
		flex: 1,
	},
	fabButton: {
		borderRadius: 30,
		width: 40,
		height: 40,
		marginTop: 10,
		marginLeft: 10,
		...getShadowStyle(20, 'rgb(59, 112, 164)'),
	},
});
export default DayReportsWeb;
